import {
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader
} from '@clientbase/clientbase-library'
import { Stack, Typography, useTheme } from '@mui/material'
import { useRouter } from 'next/router'

import { settingsTabsParams } from 'utils/tabs'
import { track } from 'utils/tracking'

import { buildSalesWhatsappUrl } from '../ModalNewPlansSmall/utils/buildSalesWhatsappUrl'
import * as S from './styles'

// Página de planos dentro do CONTA: a contratação acontece no próprio app (SC-11070).
const MY_PLAN_PATH = `/settings?tab=${settingsTabsParams.MY_PLAN}`

interface ModalNewPlansGenericProps {
  open: boolean
  onClose: () => void
  /**
   * 'free_ended' (padrão): fim do plano Free — a campanha original.
   * 'trial_ended': o período gratuito do merchant acabou e ele precisa assinar
   * para continuar usando.
   */
  variant?: 'free_ended' | 'trial_ended'
}

const COPY = {
  free_ended: {
    title: 'Mais recursos e cobranças grátis',
    lead: 'Lançamos novos planos com recursos incríveis para a sua operação continuar crescendo com a Base de Clientes.',
    highlightSubtitle: 'em todos os novos planos, via boleto e PIX',
    cta: 'Conhecer novos planos'
  },
  trial_ended: {
    title: 'Seu período gratuito acabou',
    lead: 'Para continuar usando a Base de Clientes, escolha um dos nossos planos. Seus dados, saldo e relatórios continuam disponíveis.',
    // Quem vem do trial nunca teve os planos antigos, então "novos planos" não
    // diz nada para ele.
    highlightSubtitle: 'via boleto e PIX',
    cta: 'Conhecer planos'
  }
}

const ModalNewPlansGeneric = ({
  open,
  onClose,
  variant = 'free_ended'
}: ModalNewPlansGenericProps) => {
  const theme = useTheme()
  const { push } = useRouter()
  const salesUrl = buildSalesWhatsappUrl()
  const copy = COPY[variant]

  // `upgrade_modal_viewed` chama a campanha de 'generic'; aqui ela é
  // 'free_ended'. Enviar o mesmo nome nos dois eventos é o que permite comparar
  // visualização e clique da mesma oferta.
  const trackedVariant = variant === 'trial_ended' ? 'trial_ended' : 'generic'

  const trackCta = (destination: 'plans_page' | 'sales') =>
    track('upgrade_cta_clicked', {
      variant: trackedVariant,
      destination
    })

  return (
    <Modal
      width="90vw"
      height="auto"
      maxWidth="600px"
      maxHeight="90vh"
      handleClose={onClose}
      open={open}
      size="large"
    >
      <ModalBody>
        <ModalHeader onClose={onClose} sx={{ border: 'none' }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <S.HeaderIcon>
              <Icon
                icon="workspacePremium"
                colorSX={theme.palette.primary.main}
                width="22px"
                height="22px"
              />
            </S.HeaderIcon>
            <Typography variant="lgBold">{copy.title}</Typography>
          </Stack>
        </ModalHeader>

        <ModalContent>
          <S.Wrapper>
            <S.Lead>{copy.lead}</S.Lead>

            <S.FreeBillingsHighlight>
              <S.HighlightIcon>
                <Icon icon="paid" colorSX="#fff" width="19px" height="19px" />
              </S.HighlightIcon>
              <S.HighlightText>
                <S.HighlightTitle>
                  100 cobranças grátis todos os meses
                </S.HighlightTitle>
                <S.HighlightSubtitle>
                  &nbsp;{copy.highlightSubtitle}
                </S.HighlightSubtitle>
              </S.HighlightText>
            </S.FreeBillingsHighlight>

            {variant === 'free_ended' && (
              <S.Deadline>
                Desde <strong>1º de agosto</strong>, o plano Free deixou de
                existir. Escolha o plano ideal para o seu negócio.
              </S.Deadline>
            )}

            <S.Actions>
              <S.CtaButton
                href={MY_PLAN_PATH}
                onClick={(event) => {
                  event.preventDefault()
                  trackCta('plans_page')
                  onClose()
                  push(MY_PLAN_PATH)
                }}
              >
                {copy.cta}
              </S.CtaButton>

              <S.SecondaryButton
                href={salesUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCta('sales')}
              >
                Quero falar com vendas
              </S.SecondaryButton>
            </S.Actions>
          </S.Wrapper>
        </ModalContent>
      </ModalBody>
    </Modal>
  )
}

export default ModalNewPlansGeneric
